import React, { KeyboardEventHandler, FocusEventHandler, ComponentType, memo } from 'react';
import Select, {
  SelectInstance,
  ActionMeta,
  ClassNamesConfig,
  components,
  ControlProps,
  GroupBase,
  InputActionMeta,
  MultiValue,
  MultiValueProps,
} from 'react-select';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { cn, type MultiSelectOptionType } from '@restworld/utils-common';

export const MultiSelectInput = ({
  onInputChange,
  onChange,
  onKeyDown,
  inputValue,
  value = [],
  onFocus,
  onClick,
  hasBorder = true,
  placeholder,
  autoFocus,
  isDisabled,
  displayLimit = 3,
  isClearable,
  isSearchable = true,
  selectRef,
}: IMultiSelectInputProps) => {
  // return <TextInput id="" onChange={(value) => onInputChange && onInputChange(value)} placeholder={placeholder} />;
  const components = {
    DropdownIndicator: null,
    Control,
    MultiValue: (props: MultiValueProps<MultiSelectOptionType, true, GroupBase<MultiSelectOptionType>>) => (
      <LimitedChipsContainer selectProps={props} displayLimit={displayLimit} />
    ),
  };
  const classNames: ClassNamesConfig<MultiSelectOptionType, true, GroupBase<MultiSelectOptionType>> = {
    control: (state) =>
      `p-2 !rounded-2xl !shadow-none ${hasBorder ? `border-1 !border-gray-400 hover:!shadow` : `!border-0`} ${
        state.isDisabled ? `!bg-white` : ``
      }`,
    multiValue: () => `!bg-primary-50 !text-primary-800 !rounded-lg !p-1.5 !text-sm !font-medium !text-primary-800`,
    multiValueRemove: () => `hover:!text-white`,
    multiValueLabel: () => `!text-primary-800 !text-sm`,
    container: () => 'w-full',
  };
  return (
    <StyledSelectComponent
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onClick) onClick(e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Select
        components={components}
        inputValue={inputValue}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isMulti
        menuIsOpen={false}
        value={value}
        placeholder={placeholder}
        classNames={classNames}
        onChange={onChange}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={selectRef}
      />
    </StyledSelectComponent>
  );
};

export default memo(MultiSelectInput);
const LimitedChipsContainer = memo(
  ({
    selectProps,
    displayLimit,
  }: {
    selectProps: MultiValueProps<MultiSelectOptionType, true, GroupBase<MultiSelectOptionType>>;
    displayLimit: number;
  }) => {
    const { index, getValue } = selectProps;
    const overflow = getValue()
      .slice(displayLimit)
      .map((x) => x.label);
    return index < displayLimit ? (
      <components.MultiValue {...selectProps} />
    ) : index === displayLimit ? (
      <MoreSelectedBadge items={overflow} />
    ) : null;
  }
);
const Control:
  | ComponentType<ControlProps<MultiSelectOptionType, boolean, GroupBase<MultiSelectOptionType>>>
  | undefined = ({
  // eslint-disable-next-line react/prop-types
  children,
  ...rest
}) => (
  <components.Control {...rest}>
    <MagnifyingGlassIcon className="h-6 w-6 text-gray-900 pl-1" /> {children}
  </components.Control>
);
const MoreSelectedBadge = ({ items }: { items: string[] }) => (
  <div className="w-fit rounded-lg p-[9px] bg-primary-50 font-medium text-sm text-primary-800">{`Altri ${items.length}`}</div>
);
export const createOption = (label: string, value?: string, type?: string) => ({
  label,
  value: value || label,
  type,
  key: 'selected_occupation',
});

interface IMultiSelectInputProps {
  onInputChange?: (newValue: string, actionMeta?: InputActionMeta) => void;
  onChange?: (newValue: MultiValue<MultiSelectOptionType>, actionMeta: ActionMeta<MultiSelectOptionType>) => void;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  inputValue?: string;
  value?: readonly MultiSelectOptionType[];
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  hasBorder?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  displayLimit?: number;
  isClearable?: boolean;
  isSearchable?: boolean;
  selectRef?: RefType;
}
const StyledSelectComponent = ({ className, ...props }: any) => (
  <div
    className={cn(
      'input {',
      'focus:!shadow-none focus:ring-0',
      'placeholder:truncate placeholder:text-base placeholder:font-normal border-none',
      '}',
      className
    )}
    {...props}
  />
);
export type RefType = React.MutableRefObject<SelectRefType | undefined>;
export type SelectRefType = SelectInstance<MultiSelectOptionType, true, GroupBase<MultiSelectOptionType>>;
